// src/pages/auth/VerifyEmailPage.tsx
// Email verification with token from URL.

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuthnAuth, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Alert, ErrorAlert } from '@react/components';
import { AuthLayout } from './AuthLayout';

export const VerifyEmailPage = observer(() => {
  const authnAuth = useAuthnAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState<unknown>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg(t('auth.invalidVerificationToken'));
      return;
    }

    authnAuth.VerifyEmail({ token })
      .then(() => setStatus('success'))
      .catch((err: unknown) => {
        setStatus('error');
        setErrorMsg(err);
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = async () => {
    setResending(true);
    try {
      await authnAuth.ResendVerificationEmail({ email: '' });
    } catch {
      // ignore
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle as="h1" className="text-2xl">{t('auth.verifyEmailTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {status === 'verifying' && (
            <div className="py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              <p className="text-sm text-muted-foreground mt-4">{t('auth.verifyingEmail')}</p>
            </div>
          )}
          {status === 'success' && (
            <>
              <Alert variant="success">{t('auth.emailVerified')}</Alert>
              <Link to="/login" className="text-sm text-primary hover:text-primary/80">
                {t('auth.continueToLogin')}
              </Link>
            </>
          )}
          {status === 'error' && (
            <>
              <ErrorAlert error={errorMsg} />
              <Button variant="secondary" onClick={handleResend} isLoading={resending}>
                {t('auth.resendVerification')}
              </Button>
              <Link to="/login" className="text-sm text-primary hover:text-primary/80 block">
                {t('auth.backToLogin')}
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
});

export default VerifyEmailPage;
